import * as React from 'react'
import { ExternalLink } from 'lucide-react'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Button } from '@/components/ui/button'
import { SectionHeader } from '@/components/ui/page-header'
import { ErrorState } from '@/components/ui/states'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { instantLabel } from '@/lib/instantLabel'
import { useEventGhl, useSetEventGhlLinks } from '@/hooks/useGoHighLevel'
import type { EventDetailDto } from '@lib/dto/event.dto'
import { GhlLinkDialog } from './GhlLinkDialog'
import { GHL_LINK_VIA_LABEL } from '@lib/enums/ghl-link-via.enum'
import type { EventGhlHistoryDto, EventGhlLinkDto } from '@lib/dto/gohighlevel.dto'

/**
 * The GoHighLevel leads an event came from, and how it was linked to them. Who
 * the couple is comes from GoHighLevel as the section opens; when it can't be
 * reached the link is still shown, by sub-account. Linking, replacing and
 * unlinking are for staff who can edit events.
 */
export function EventGhlPanel({
  event,
  canEdit,
}: {
  event: EventDetailDto
  /** Whether the viewer can edit events, and so link the event to a lead, replace it, or unlink it. */
  canEdit: boolean
}) {
  const ghl = useEventGhl(event.id, true)
  const unlink = useSetEventGhlLinks(event.id, event.venueId)
  const [linking, setLinking] = React.useState(false)
  const [confirmUnlink, setConfirmUnlink] = React.useState(false)

  if (ghl.isPending) return <p className="m-0 text-sm text-ink-muted">Checking GoHighLevel…</p>
  if (ghl.isError) return <ErrorState message={requestErrorMessage(ghl.error)} onRetry={() => void ghl.refetch()} />
  const { links, history } = ghl.data

  return (
    <div className="space-y-6">
      {links.length === 0 ? (
        <Card className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <p className="m-0 text-sm text-ink-muted">Not linked to a GoHighLevel lead.</p>
          {canEdit ? (
            <Button size="sm" onClick={() => setLinking(true)}>
              Link to a GoHighLevel lead
            </Button>
          ) : null}
        </Card>
      ) : (
        <div className="space-y-2">
          <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
            {links.map((link) => (
              <LinkRow key={`${link.locationId}:${link.opportunityId}`} link={link} />
            ))}
          </ul>
          {canEdit ? (
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setLinking(true)}>
                Replace
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmUnlink(true)}>
                Unlink
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {/* The couples to choose from are read only for someone who can link one. */}
      {canEdit ? (
        <>
          <GhlLinkDialog event={event} open={linking} onOpenChange={setLinking} />
          <ConfirmDialog
            open={confirmUnlink}
            onOpenChange={setConfirmUnlink}
            title="Unlink this event from GoHighLevel?"
            description="The couple will be offered again when creating events. Nothing changes in GoHighLevel."
            confirmLabel="Unlink"
            destructive
            busy={unlink.isPending}
            error={unlink.isError ? requestErrorMessage(unlink.error) : null}
            onConfirm={() => unlink.mutate({ opportunities: [] }, { onSuccess: () => setConfirmUnlink(false) })}
          />
        </>
      ) : null}

      {history.length > 0 ? (
        <section className="space-y-2">
          <SectionHeader title="History" />
          <ul className="m-0 list-none space-y-1 p-0">
            {history.map((h) => (
              <li key={`${h.action}:${h.locationId}:${h.opportunityId}:${h.at}`} className="text-sm text-ink-muted">
                {historyLine(h)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

function LinkRow({ link }: { link: EventGhlLinkDto }) {
  const who = link.couple?.name ?? (link.locationName !== null ? `Lead in ${link.locationName}` : 'Lead in GoHighLevel')
  const meta = [
    // Already the title when GoHighLevel couldn't say who the couple is.
    link.couple !== null ? link.locationName : null,
    `${GHL_LINK_VIA_LABEL[link.via]}${link.linkedByName !== null ? ` by ${link.linkedByName}` : ''} on ${instantLabel(link.linkedAt)}`,
  ]
    .filter((part) => part !== null)
    .join(' · ')
  return (
    <li className="flex items-center gap-3 px-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="m-0 truncate text-sm font-medium text-ink">{who}</p>
        {link.couple?.email ? <p className="m-0 truncate text-xs text-ink-muted">{link.couple.email}</p> : null}
        <p className="m-0 truncate text-xs text-ink-muted">{meta}</p>
      </div>
      <Button variant="outline" size="sm" asChild>
        <a href={link.openUrl} target="_blank" rel="noopener noreferrer">
          Open in GoHighLevel <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </Button>
    </li>
  )
}

/** "Linked by Jane Doe (picked when creating the event) — 9 Oct 2026, 3:04 pm". */
function historyLine(h: EventGhlHistoryDto): string {
  const verb = h.action === 'linked' ? 'Linked' : 'Unlinked'
  const by = h.byName !== null ? ` by ${h.byName}` : ''
  const how = h.via !== null ? ` (${GHL_LINK_VIA_LABEL[h.via]})` : ''
  return `${verb}${by}${how} — ${instantLabel(h.at)}`
}
